'use client';

import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useRouter } from 'next/navigation';

import { Card, Separator, Skeleton } from '@mokoboko/components';
import { Wallet, Calendar, CreditCard, Gift } from 'lucide-react';
import {
  giftSpendDisplay,
  wholeOrderGiftBalanceLine,
} from '@/helpers/gift-card';
import { formatAmount } from '@/lib/utils';
import { Order } from '@/lib/types';
import { formatClassCount } from '@/helpers/credits';
import { useAuth } from '@/contexts/auth';
import { LoginDialog } from '@/components/dialogs/login';
import { CreateAccountDialog } from '@/components/dialogs/create-account';
import {
  PostPurchaseActions,
  PostPurchaseActionsSkeleton,
} from './post-purchase-actions';
import {
  ReceiptEmailRow,
  ReceiptRow,
  ReceiptRowSkeleton,
  ReceiptStatusHeader,
} from './receipt-rows';

export const ClassPackOrderReceipt = ({ order }: { order: Order }) => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();
  const [activeModal, setActiveModal] = useState<
    'login' | 'createAccount' | null
  >(null);

  const { class_pack_order_items = [], user, coupon_code } = order;

  const totalClasses = useMemo(() => {
    return class_pack_order_items.reduce(
      (sum, item) => sum + (item.class_pack?.total_number_of_classes ?? 0),
      0
    );
  }, [class_pack_order_items]);

  const firstPackCode = class_pack_order_items[0]?.class_pack_code;
  const firstPack = class_pack_order_items[0]?.class_pack;
  const validUntil = firstPackCode?.expires_at
    ? format(new Date(firstPackCode.expires_at), 'd MMM, yyyy')
    : null;

  const totalQuantity = class_pack_order_items.length;
  const classesPerPack = firstPack?.total_number_of_classes ?? 0;

  // A money card paid part or all of it (gift cards M1): a payment, never a
  // discount, so the pack shows its price, then what the card paid and what
  // the card was charged for the rest, as a class receipt does.
  const giftSpend = order.gift_card ?? null;
  const giftSpendLines = giftSpend && giftSpendDisplay(giftSpend, order.currency);
  const giftBalanceLine =
    giftSpendLines &&
    wholeOrderGiftBalanceLine(giftSpendLines, order.payment_status);
  const isDiscounted =
    !giftSpend &&
    order.final_amount != null &&
    order.final_amount !== order.total_amount;
  const finalPriceLabel = formatAmount(
    giftSpend ? order.total_amount : (order.final_amount ?? order.total_amount),
    order.currency
  );

  const handleMyAccountClick = () => {
    if (isAuthenticated) {
      router.push('/dashboard');
    } else {
      setActiveModal('login');
    }
  };

  return (
    <>
      <div className="bg-white">
        <ReceiptStatusHeader
          status="confirmed"
          statusText="Pack bought"
          title={firstPack?.name ?? 'Class Pack'}
        />

        <Separator className="my-5 sm:my-6" />

        <div className="flex flex-col gap-4">
          <ReceiptRow icon={Wallet} title={formatClassCount(totalClasses)}>
            <p className="mt-1 text-sm text-gray-600">
              {isDiscounted && (
                <>
                  <span className="text-gray-400 line-through">
                    {formatAmount(order.total_amount, order.currency)}
                  </span>{' '}
                </>
              )}
              {finalPriceLabel}
              {totalQuantity > 1 && ` (x${totalQuantity})`}
              {order.discount_amount > 0 && (
                <>
                  {' · '}
                  {order.discount_percentage > 0
                    ? `${order.discount_percentage}% off`
                    : `${formatAmount(order.discount_amount, order.currency)} off`}
                  {coupon_code && ` (${coupon_code})`}
                </>
              )}
              {totalQuantity > 1 && (
                <>
                  {' · '}
                  {classesPerPack} per pack x {totalQuantity}
                </>
              )}
            </p>
          </ReceiptRow>

          {giftSpendLines && (
            <ReceiptRow icon={Gift} title={`Gift card ${giftSpendLines.applied}`}>
              {giftBalanceLine && (
                <p className="mt-1 text-sm text-gray-600">{giftBalanceLine}</p>
              )}
            </ReceiptRow>
          )}

          {giftSpend && giftSpend.remainder > 0 && giftSpendLines && (
            <ReceiptRow icon={CreditCard} title={giftSpendLines.remainder}>
              <p className="mt-1 text-sm text-gray-600">Paid</p>
            </ReceiptRow>
          )}

          {validUntil && (
            <ReceiptRow icon={Calendar} title={validUntil}>
              <p className="mt-1 text-sm text-gray-600">Valid until</p>
            </ReceiptRow>
          )}

          {user?.email && <ReceiptEmailRow email={user.email} />}
        </div>

        <Separator className="my-6" />

        <PostPurchaseActions
          isAuthenticated={isAuthenticated}
          onLoginClick={() => setActiveModal('login')}
          onBookClass={() => router.push('/classes')}
          secondaryLabel="My account"
          onSecondaryClick={handleMyAccountClick}
        />
      </div>

      {activeModal === 'login' && (
        <LoginDialog
          open={activeModal === 'login'}
          onClose={() => setActiveModal(null)}
          onConfirm={() => setActiveModal(null)}
          onCreateAccountLinkClick={() => setActiveModal('createAccount')}
        />
      )}

      {activeModal === 'createAccount' && (
        <CreateAccountDialog
          open={activeModal === 'createAccount'}
          onClose={() => setActiveModal(null)}
          onLoginLinkClick={() => setActiveModal('login')}
          onConfirm={async () => setActiveModal(null)}
        />
      )}
    </>
  );
};

export const ClassPackReceiptSkeleton = () => {
  return (
    <Card contentClassName="p-5 sm:p-6 flex flex-col gap-5 sm:gap-6">
      <div className="flex items-center justify-between gap-4">
        <Skeleton isRounded className="!h-11 w-[140px] sm:!h-10 sm:w-[124px]" />
      </div>

      <Separator />

      <div className="flex flex-col items-center">
        <Skeleton isCircle height={80} />
        <div className="mt-3">
          <Skeleton text="body-sm" width={88} />
        </div>
        <div className="mt-1.5">
          <Skeleton text="h2" width={208} />
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <ReceiptRowSkeleton key={i} />
        ))}
      </div>

      <Separator />

      <PostPurchaseActionsSkeleton />
    </Card>
  );
};
